import React from 'react'
import { Modal, Tree } from 'antd'
import styled from 'styled-components'
import { FileOutlined, PlayCircleOutlined, CheckCircleOutlined } from '@ant-design/icons'
import ComponentTitle from '@/pages/safety-equipment/widgets/component-title'
import { t } from '@/i18n'

interface PropsType {
  log: string
  visible: boolean
  onCancel: () => void
}

interface LogNode {
  title: React.ReactNode
  key: string
  children?: LogNode[]
}

const stepKeywords = [
  // 阶段标记
  { key: 'phaseStart', label: t('阶段开始'), match: /phase start/i },
  { key: 'phaseEnd', label: t('阶段结束'), match: /phase end/i },

  // 插件初始化
  { key: 'pluginInit', label: t('插件初始化'), match: /plg setup passed|plugin init|插件初始化/i },

  // 命令构造与执行
  {
    key: 'commandConstruct',
    label: t('命令构造'),
    match: /execution orders for the current construction|构造的执行命令|constructed command/i,
  },
  { key: 'commandExec', label: t('命令执行'), match: /run clean command|执行命令|command:/i },

  // 恶意命令提示
  { key: 'maliciousCommand', label: t('恶意命令提示'), match: /malicious command/i },

  // 进程相关
  {
    key: 'processCreate',
    label: t('进程创建'),
    match:
      /process \d+ is generated for the program running|new process event traced|新进程|process created/i,
  },
  { key: 'processTrace', label: t('进程追踪'), match: /traced|追踪/i },
  {
    key: 'processExit',
    label: t('进程退出'),
    match: /process\(pid=\d+\) does not exist|exited|failed to terminate/i,
  },

  // 文件相关
  { key: 'fileLoad', label: t('文件加载'), match: /loading oss file|加载oss文件|file load/i },
  { key: 'fileWrite', label: t('文件写入'), match: /write the file|写入文件/i },
  { key: 'fileDelete', label: t('文件删除'), match: /has been deleted|删除文件/i },

  // 缓存
  { key: 'cache', label: t('缓存操作'), match: /add by pid to cache|cache table|cache/i },

  // 检测
  { key: 'detectStart', label: t('检测开始'), match: /开始检测|starting file detection|检测开始/i },
  {
    key: 'detectResult',
    label: t('检测结果'),
    match: /检测完成|detection complete|检测结果|result returned/i,
  },

  // 等待
  { key: 'wait', label: t('等待'), match: /wait|等待/i },

  // 状态变更
  {
    key: 'statusUpdate',
    label: t('状态变更'),
    match: /update task status|状态变更|status update/i,
  },

  // 任务结果上报
  {
    key: 'resultReport',
    label: t('任务结果上报'),
    match: /report task result to portal|report task/i,
  },

  // 事件上报
  { key: 'eventReport', label: t('事件上报'), match: /report event/i },

  // 线程退出
  { key: 'threadExit', label: t('线程退出'), match: /thread has exited|线程退出/i },

  // 标准输出
  { key: 'stdout', label: t('标准输出'), match: /stdout when the program running is/i },

  // 其它
  { key: 'other', label: t('其它'), match: /.*/ }, // 兜底
]

function extractTime(line: string) {
  const match = line.match(/(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2},\d{3})/)
  return match ? match[1] : ''
}

function extractFileType(line: string) {
  if (/\.exe/.test(line)) return 'exe'
  if (/sha256:/i.test(line)) return 'SHA256'
  if (/sha1:/i.test(line)) return 'SHA1'
  if (/md5:/i.test(line)) return 'MD5'
  if (/路径|path:/i.test(line)) return t('路径')
  return ''
}

function parseLogToTree(log: string): LogNode[] {
  const lines = log.split('\n').filter(Boolean)
  const phaseMap: { [key: string]: { label: string; icon: React.ReactNode; color: string } } = {
    setup: {
      label: 'setup phase',
      icon: <PlayCircleOutlined style={{ color: '#1890ff' }} />, // 蓝色
      color: '#e6f7ff',
    },
    run: {
      label: 'run phase',
      icon: <FileOutlined style={{ color: '#52c41a' }} />, // 绿色
      color: '#f6ffed',
    },
    cleanup: {
      label: 'cleanup phase',
      icon: <CheckCircleOutlined style={{ color: '#faad14' }} />, // 橙色
      color: '#fffbe6',
    },
  }
  const root: LogNode = {
    title: t('执行过程'),
    key: 'root',
    children: [],
  }
  let currentPhase: LogNode | null = null
  let currentSteps: { [step: string]: LogNode } = {}
  let phaseTime = ''
  let rootSteps: { [step: string]: LogNode & { firstTime?: string } } = {}

  lines.forEach((line, idx) => {
    const phaseMatch = line.match(/[- ]+([a-z ]+phase) (start|end)[- ]*$/i)
    if (phaseMatch) {
      if (phaseMatch[2] === 'start') {
        const phaseKey = phaseMatch[1].split(' ')[0].toLowerCase()
        const phaseInfo = phaseMap[phaseKey] || { label: phaseMatch[1], icon: null, color: '#fff' }
        phaseTime = extractTime(line)
        currentPhase = {
          title: (
            <PhaseTitle color={phaseInfo.color}>
              {phaseInfo.icon}
              <span className="phase-label">{phaseInfo.label}</span>
              {phaseTime && <span className="phase-time">{phaseTime}</span>}
            </PhaseTitle>
          ),

          key: `phase-${idx}`,
          children: [],
        }
        root.children!.push(currentPhase)
        currentSteps = {}
      } else {
        currentPhase = null
        currentSteps = {}
      }
      return
    }
    const step = stepKeywords.find(s => s.match.test(line)) || stepKeywords[stepKeywords.length - 1]
    const logTime = extractTime(line)
    const logNode = {
      title: (
        <LogLine dangerouslySetInnerHTML={{ __html: highlightLog(line.replace(/^.*?\] /, '')) }} />
      ),

      key: `line-${idx}`,
    }
    if (currentPhase) {
      if (!currentSteps[step.key]) {
        currentSteps[step.key] = {
          title: (
            <StepTitle>
              {step.label}
              {logTime && <span className="step-time">{logTime}</span>}
            </StepTitle>
          ),

          key: `${currentPhase.key}-step-${step.key}`,
          children: [],
        }
      }
      currentSteps[step.key].children!.push(logNode)
      // 更新label时间为该分组第一条日志的时间
      if (currentSteps[step.key].children!.length === 1 && logTime) {
        currentSteps[step.key].title = (
          <StepTitle>
            {step.label}
            <span className="step-time">{logTime}</span>
          </StepTitle>
        )
      }
      if (!currentPhase.children!.includes(currentSteps[step.key])) {
        currentPhase.children!.push(currentSteps[step.key])
      }
    } else {
      if (!rootSteps[step.key]) {
        rootSteps[step.key] = {
          title: (
            <StepTitle>
              {step.label}
              {logTime && <span className="step-time">{logTime}</span>}
            </StepTitle>
          ),

          key: `root-step-${step.key}`,
          children: [],
        }
        root.children!.push(rootSteps[step.key])
      }
      rootSteps[step.key].children!.push(logNode)
      // 更新label时间为该分组第一条日志的时间
      if (rootSteps[step.key].children!.length === 1 && logTime) {
        rootSteps[step.key].title = (
          <StepTitle>
            {step.label}
            <span className="step-time">{logTime}</span>
          </StepTitle>
        )
      }
    }
  })
  return [root]
}

function highlightLog(text: string) {
  return (
    text
      // 高亮 "SHA256":"xxxx"
      .replace(
        /(["']sha256["']\s*:\s*["']?[a-fA-F0-9]{32,}["']?)/gi,
        '<span class="sha256">$1</span>',
      )
      // 高亮 "pname":"xxx.exe"
      .replace(/(["']pname["']\s*:\s*["'][\w\-.]+["'])/gi, '<span class="pname">$1</span>')
      // 高亮完整 Windows 路径
      .replace(/([a-zA-Z]:\\[\\\w\-. ]+\.exe)/g, '<span class="cmdfile">$1</span>')
      // 高亮pname:xxx.exe 或 pname:xxx
      .replace(/\b(pname:[\w\-.]+)\b/gi, '<span class="pname">$1</span>')
      // 高亮pid/ppid
      .replace(/\b(pid:\d+)\b/gi, '<span class="pid">$1</span>')
      .replace(/\b(ppid:\d+)\b/gi, '<span class="pid">$1</span>')
      // 兼容单独的xxx.exe（非路径）
      .replace(/\b([\w\-]+\.exe)\b/gi, '<span class="cmdfile">$1</span>')
  )
}

const PhaseTitle = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  font-weight: bold;
  background: ${props => props.color};
  border-radius: 4px;
  padding: 2px 8px;
  margin: 2px 0 4px 0;
  .phase-label {
    margin-left: 6px;
    font-size: 15px;
    color: #333;
  }
  .phase-time {
    margin-left: 8px;
    font-size: 13px;
    color: #888;
    font-weight: normal;
  }
`
const StepTitle = styled.div`
  font-size: 13px;
  color: #1890ff;
  font-weight: 500;
  margin: 2px 0 2px 0;
  display: flex;
  align-items: center;
  .step-time {
    margin-left: 8px;
    color: #888;
    font-size: 12px;
    font-weight: normal;
  }
`
const LogLine = styled.div.attrs({ className: 'log-line' })`
  font-size: 13px;
  color: #555;
  padding-left: 8px;
  padding-top: 1px;
  padding-bottom: 1px;
  word-break: break-all;
  .pid {
    color: #13c2c2;
    font-weight: bold;
  }
  .pname {
    color: #722ed1;
    font-weight: bold;

    .cmdfile {
      color: #722ed1;
    }
  }
  .cmdfile {
    color: #fa541c;
    font-weight: bold;
    text-decoration: underline;
  }

  .sha256 {
    color: #fa8c16;
    font-weight: bold;
  }
`

const StyledTreeWrapper = styled(Modal)`
  .ant-modal-content {
    border-radius: 8px;
    position: relative;

    .ant-modal-close {
      width: 36px;
      height: 36px;
      background: #d8d6f4;
      box-shadow: -1px 4px 10px 0px #c7c7c7;
      border-radius: 6px;
      border: none;
      color: #4238c9;
      font-size: 16px;
      font-weight: 500;
      position: absolute;
      right: -8px;
      top: -8px;
      transition: transform 0.3s ease;

      &:hover {
        transform: translate(-18px, 18px);
        box-shadow: 0 0 0 0;
      }

      .ant-modal-close-x {
        width: 100%;
        height: 100%;
        line-height: 36px;
        text-align: center;
      }
    }

    .ant-modal-header {
      height: 56px;
      background: #f2f0ff;
      border-bottom: none;
      margin: 0;
      padding: 19px 20px;
    }

    .ant-modal-body {
      max-height: 80vh;
      padding: 24px;
      border-radius: 8px;
      overflow-y: auto;

      .title {
        font-size: 16px;
        font-weight: 500;
        color: #333;
        margin-bottom: 12px;
      }

      .ant-tree {
        background: transparent;
      }
      .ant-tree-treenode {
        margin-bottom: 2px;
      }
      .ant-tree-title {
        white-space: pre-wrap;
      }
    }
  }
`

const ExecutionProcessTree: React.FC<PropsType> = ({ log, visible, onCancel }) => {
  const treeData = React.useMemo(() => parseLogToTree(log), [log])
  return (
    <StyledTreeWrapper
      zIndex={1001}
      title={<ComponentTitle title={t('ETW进程树分析')} />}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
      centered
    >
      <div className="title">{t('进程树信息')}</div>
      <Tree treeData={treeData} defaultExpandAll showLine />
    </StyledTreeWrapper>
  )
}

export default ExecutionProcessTree
